import React, { useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { SmartCardProvider } from '@atlaskit/link-provider';
import { Text } from '@atlaskit/primitives/compiled';
import { Card as SmartCard } from '@atlaskit/smart-card';
import {
	type ActionKeys,
	JSM_CREATE_EMBEDDED_REQUEST_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { HELP_CENTER_FORM_REQUEST_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/help-center-form-request-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { NotifySuccessCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/jsm-on-request-submitted-command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import { addQueryParam } from '../../common/utils';

import messages from './messages';

export const JsmCreateEmbeddedRequestAction = (
	props: ActionRendererFor<typeof ActionKeys.JsmCreateEmbeddedRequest>,
): React.JSX.Element | null => {
	const { onActioned } = props;
	const action = props.actions?.[0];
	const isActionEnded = props.getReadonly(action);
	const actionStatus = props.getStatus(action);
	const url = action?.data?.url;
	const bridgeAPI = getBrowserBridgeAPI();
	const { formatMessage } = useIntl();
	const createdRequestUrl = useRef<string | null>(null);
	useEffect(() => {
		if (!action) {
			return;
		}
		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, NotifySuccessCommand, (command) => {
			if (command.action !== action.invocationId) {
				return;
			}
			createdRequestUrl.current = command.requestUrl;
			onActioned(
				action,
				`${formatMessage(messages.jsmRequestCreatedDescription)} \n ${command.requestUrl}`,
			);
		});
		// handlers have been setup - now we can mark ourselves as 'ready' to handle incoming messages
		bridgeAPI.ready(ROVO_CHAT_CLIENT_ID);
	}, [bridgeAPI, onActioned, action, formatMessage]);

	const senderClientId: string = HELP_CENTER_FORM_REQUEST_CLIENT_ID;

	// Callback ref to set attributes when iframe is mounted
	const onIframeRefAvailable = useCallback(
		(iframe: HTMLIFrameElement | null) => {
			if (iframe) {
				// needed for BRIDGE API to work across cross origin
				iframe.setAttribute('data-rovo-receiver-id', senderClientId);
			}
		},
		[senderClientId],
	);

	if (!action || !url) {
		return null;
	}
	const urlWithActionParam = addQueryParam(url, 'rovoChatTriggeringAction', action.invocationId);
	if (actionStatus === 'errored' && createdRequestUrl.current) {
		// if some how there is an error while resolving the action,
		// we still want to show the user the request url because it was created successfully just not recorded in conversation assistant
		return (
			<Text>
				{formatMessage(messages.jsmRequestCreatedDescription)} {createdRequestUrl.current}
			</Text>
		);
	}
	if (isActionEnded) {
		return null;
	}

	return (
		<SmartCardProvider>
			<SmartCard
				key={urlWithActionParam}
				appearance="embed"
				url={urlWithActionParam}
				showHoverPreview
				embedIframeRef={onIframeRefAvailable}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				onClick={(event: any) => {
					event.preventDefault();
					window.open(urlWithActionParam, '_blank', 'noopener, noreferrer');
				}}
			/>
		</SmartCardProvider>
	);
};

export const filterJsmCreateEmbeddedRequestActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JsmCreateEmbeddedRequest]>[] => {
	return actions.filter(assertIsJsmCreateEmbeddedRequestAction) as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.JsmCreateEmbeddedRequest]
	>[];
};

const assertIsJsmCreateEmbeddedRequestAction = (action: MessageAction): MessageAction => {
	if (action.key !== JSM_CREATE_EMBEDDED_REQUEST_KEY) {
		throw new Error('Invalid action key for JsmCreateRequestAction');
	}
	return action;
};
